import { FormikHelpers } from 'formik';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useState } from 'react';
import { submitForm } from '@jd/client/form/submit';
import { useRouter } from 'next/navigation';
import { ConditionalRedirect, FormField } from '../type';
import { formatSubmissionData } from './fieldFormatter';
import { splitFiles } from './files/splitFiles';
import { useFireConversion } from './tracking/useFireConversion';
import type {
  GoogleAdsTrackingConfig,
  MetaTrackingConfig,
} from './tracking/types';
import { resolveRedirect } from './conditions/resolveRedirect';
import { useLocale } from '@jd/i18n';
import { trackEvent } from '../../../../utils/trackEvent';

type SubmitStatus = {
  error?: string;
  success?: string;
  submitting?: boolean;
  redirecting?: boolean;
};

type Props = {
  id?: string;
  fields: FormField[];
  withRecaptcha?: boolean;
  redirect?: string;
  metaTracking?: MetaTrackingConfig;
  googleAdsTracking?: GoogleAdsTrackingConfig;
  conditionalRedirects?: ConditionalRedirect[];
};

const useSubmit = ({
  id,
  withRecaptcha,
  redirect,
  fields,
  metaTracking,
  googleAdsTracking,
  conditionalRedirects,
}: Props) => {
  const locale = useLocale();
  const router = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [status, setStatus] = useState<SubmitStatus>({});
  const fireConversion = useFireConversion({
    formId: id,
    metaTracking,
    googleAdsTracking,
  });
  const onSubmit = async (
    values: Record<string, any>,
    bag: FormikHelpers<any>,
  ) => {
    if (!id) return;
    try {
      setStatus({ submitting: true });
      let token = '';
      if (withRecaptcha) {
        token = (await executeRecaptcha?.('dynamic_form')) || '';
        if (!token) throw new Error('Recaptcha failed');
      }

      const { values: dataValues, files } = splitFiles(values, fields);
      const formattedValues = formatSubmissionData(dataValues, fields);
      await submitForm({
        formId: id,
        recaptchaToken: token,
        data: formattedValues,
        locale,
        files,
      });

      setStatus({ success: 'Form submitted successfully.' });
      bag.resetForm();
      await fireConversion(formattedValues);
      trackEvent('form_submit', { form_id: id });
      trackEvent('generate_lead', {
        form_id: id,
      });
      const redirectUrl = resolveRedirect(
        conditionalRedirects,
        formattedValues,
        redirect,
      );
      if (redirectUrl) {
        setStatus((prev) => ({ ...prev, redirecting: true }));
        router.push(redirectUrl);
      }
    } catch (error) {
      setStatus({ error: 'Failed to submit form. Please try again.' });
    }
  };

  return {
    status,
    onSubmit,
    isLoading: !!status.submitting || !!status.redirecting,
  };
};
export default useSubmit;
